/**
 * HOLDINGS API WITH OPTIMISTIC UPDATES
 *
 * This API implements sophisticated optimistic update patterns for holdings management,
 * providing immediate UI feedback while maintaining data consistency and error handling.
 *
 * KEY FEATURES:
 * ✅ Instant UI updates (no loading states)
 * ✅ Automatic cache synchronization across multiple views
 * ✅ Graceful error handling with automatic rollback
 * ✅ Race condition prevention for rapid user interactions
 * ✅ Cache-first approach to avoid stale data issues
 *
 * OPTIMISTIC UPDATE PATTERN:
 * 1. User interaction (e.g., edit cost basis) triggers mutation
 * 2. Cache updates immediately → UI shows new values instantly
 * 3. Server request happens in background
 * 4. Success → Changes persist | Failure → Automatic rollback
 *
 * CACHE MANAGEMENT:
 * - Updates ALL relevant cache entries (getHoldingDetails + getHoldings)
 * - Handles complex query parameter combinations automatically
 * - Cache expires after 60 seconds of non-use (RTK Query default)
 * - No persistence across browser sessions
 *
 * SAFETY MEASURES:
 * - Automatic rollback on server failures
 * - Cache-first approach prevents race conditions
 * - Server fallback for cache misses
 * - No cache pollution or stale data issues
 *
 * This implementation follows RTK Query best practices for optimistic updates:
 * https://redux-toolkit.js.org/rtk-query/usage/optimistic-updates
 */

import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { buildRoleAwarePrefix } from '@/utils/api';
import type { Role } from '@/models/AccountEnums';
import { getCachedEntriesForEndpoint } from '@/utils/rtkQueryCache';
import {
  createApi,
  type TypedUseQueryHookResult,
  type TypedUseQuerySubscriptionOptions,
} from '@reduxjs/toolkit/query/react';
import { ReactNode } from 'react';
import { RecordResponse } from '../models/ServiceTypes';
import { isLiveAsOfDate, toApiDateParam } from '@/utils/date';
import { mergeViewingTagIntoArg } from '@/utils/viewingInvestorTagMerge';
import type { ViewingTagSelection } from '@/utils/viewingTagSelection';
import { waServicesQuery } from './baseQueries';
import {
  confirmedViewingTagSelection,
  selectQueryViewingTag,
} from './selectQueryViewingTag';
import { WARootState } from './store';

// Helper utilities moved to utils/api.ts

// Define the HoldingViewModel type
export interface HoldingViewModel {
  HoldingId: number;
  Name: string;
  SecurityDescription?: string;
  AccountName: string;
  AccountId: number;
  InstitutionName: string;
  InstitutionId: number;
  Balance: number;
  Quantity: number;
  Price: number;
  CostBasis?: number;
  PercentOfAccount: number;
  Classifications: string;
  WealthHoldingType: string;
  UpdatedDate: string;
  CreatedDate: string;
  HasTaxLots: boolean;
  CanEditCostBasis: boolean;
  WealthType: number;
  UnrealizedGains?: number;
  RealizedGains?: number;
  Symbol?: string;
  CUSIP?: string;
  MaturityDate?: string;
  InterestRate?: number;
  AnnualInterestAmount?: number;
  EstimatedIncome?: number;
  /** Point-in-time yield % (holdings list). */
  YieldToMarket?: number | null;
  /** Current yield % (holdings list). */
  CurrentYield?: number | null;
  /** Yield to maturity % (holdings list). */
  YieldToMaturity?: number | null;
}

// Historical trend data point interface
export interface HoldingsTrendDataPoint {
  Date: string;
  Value: number;
}

// Historical trend holding interface
export interface HoldingsTrendItem {
  name: string;
  id: number;
  data: HoldingsTrendDataPoint[];
}

// Unrealized gains record interface
export interface UnrealizedGainsRecord {
  WealthHoldingId: number;
  WealthAccountId: number;
  WealthId: number;
  HoldingName: string;
  AccountName: string;
  WealthName: string;
  Symbol?: string;
  CUSIP?: string;
  NumberOfTaxLots: number;
  TaxLotNumber?: number | null;
  Quantity: number;
  Price: number;
  CostBasis: number;
  CurrentBalance: number;
  DollarChange: number;
  PercentChange: number;
  OpenDate: string;
  TaxLotTerm: number;
  TaxLotTermDisplay: string;
  GainType: number;
  GainTypeDisplay: string;
  DiversificationId?: number | null;
  DiversificationName?: string | null;
  SubDiversificationId?: number | null;
  SubDiversificationName?: string | null;
}

// Realized gains record interface (extends unrealized gains with additional fields)
export interface RealizedGainsRecord extends UnrealizedGainsRecord {
  SaleType?: string;
  TradeDate?: string;
  SaleDate?: string;
  SalePrice?: number;
}

// Realized gains summary interface
export interface RealizedGainsSummaryResponse {
  ShortTerm: number;
  LongTerm: number;
  SalePrice: number;
  CostBasis: number;
  CurrentBalance: number;
  UnrealizedCostBasis: number;
}

// Sort parameter interface
export interface SortParameter {
  Property: string;
  Direction: string;
  Order: number;
}

// Holdings query parameters interface
export interface HoldingsQueryParams {
  searchTerm?: string;
  page?: number;
  asOfDate?: string;
  sort?: SortParameter[];
  accountId?: number;
  ExcludedAccounts?: string;
  ExcludedAccountTypes?: string;
  ExcludedCategories?: string;
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
  OnlyPrimaryInvestors?: boolean;
  // Allow bracketed query param keys like "sort[0].Property"
  [key: string]: string | number | boolean | SortParameter[] | undefined;
}

export function mergeViewingInvestorTagIntoHoldingsArg(
  arg: HoldingsQueryParams,
  selection: ViewingTagSelection,
): HoldingsQueryParams {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

// Holdings historical trend query parameters interface
export interface HoldingsHistoricalTrendQueryParams {
  holdingId?: number;
  startDate?: string;
  endDate?: string;
  asOfDate?: string;
  page?: number;
  limit?: number;
  ExcludedAccounts?: string;
  ExcludedAccountTypes?: string;
  ExcludedCategories?: string;
  InvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
}

/**
 * Merges the global viewing-tag selection into holdings historical trend args.
 * Skips when `InvestorId` is set — same rules as `mergeViewingInvestorTagIntoHoldingsArg`.
 */
export function mergeViewingInvestorTagIntoHoldingsHistoricalTrendArg(
  arg: HoldingsHistoricalTrendQueryParams,
  selection: ViewingTagSelection,
): HoldingsHistoricalTrendQueryParams {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export interface HoldingHistoricalTrendQueryParams {
  holdingId: number;
  startDate: string;
  endDate: string;
  searchTerm?: string;
  page?: number;
  asOfDate?: string;
  sort?: SortParameter[];
  investorId?: number;
}

// Portfolio-level unrealized gains query parameters interface
export interface PortfolioUnrealizedGainsQueryParams {
  limit?: number;
  sort?: Array<{
    property: string;
    direction: 'asc' | 'desc';
    order: number;
  }>;
  InvestorId?: number;
  AccountId?: number;
  InvestorTagId?: number;
  TagId?: number;
  ExcludedAccounts?: string;
  ExcludedAccountTypes?: string;
  ExcludedCategories?: string;
  groupValueProperty?: string;
  groupDisplayProperty?: string;
}

// Portfolio-level realized gains query parameters interface
export interface PortfolioRealizedGainsQueryParams extends PortfolioUnrealizedGainsQueryParams {
  startDate: string;
  endDate: string;
  limit?: number;
}

export function mergeViewingInvestorTagIntoPortfolioGainsArg<
  T extends PortfolioUnrealizedGainsQueryParams,
>(arg: T, selection: ViewingTagSelection): T {
  if (arg.InvestorId !== undefined || arg.AccountId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export function buildPortfolioGainsSubscriptionArg<
  T extends PortfolioUnrealizedGainsQueryParams,
>(arg: T, selection: ViewingTagSelection): T {
  return mergeViewingInvestorTagIntoPortfolioGainsArg(arg, selection);
}

function buildPortfolioGainsRequest(
  currentRole: Role | undefined,
  params: PortfolioUnrealizedGainsQueryParams,
  endpoint: string,
): { url: string; queryParams: Record<string, unknown> } {
  const queryParams: Record<string, unknown> = {};

  params.sort?.forEach((sortItem, index) => {
    queryParams[`sort[${String(index)}].Property`] = sortItem.property;
    queryParams[`sort[${String(index)}].Direction`] = sortItem.direction;
    queryParams[`sort[${String(index)}].Order`] = sortItem.order;
  });
  if (typeof params.limit !== 'undefined') {
    queryParams.limit = params.limit;
  }
  if (params.groupValueProperty) {
    queryParams['Group.ValueProperty'] = params.groupValueProperty;
    queryParams['Group.DisplayProperty'] =
      params.groupDisplayProperty ?? params.groupValueProperty;
  }

  if (typeof params.AccountId === 'number') {
    // The advisor Accounts/{id} gains routes reject a request without InvestorId.
    if (typeof params.InvestorId === 'number') {
      queryParams.InvestorId = params.InvestorId;
    }
    return {
      url: `/api/${String(currentRole)}/Accounts/${String(params.AccountId)}/${endpoint}`,
      queryParams,
    };
  }

  if (params.ExcludedAccounts) {
    queryParams.ExcludedAccounts = params.ExcludedAccounts;
  }
  if (params.ExcludedAccountTypes) {
    queryParams.ExcludedAccountTypes = params.ExcludedAccountTypes;
  }
  if (params.ExcludedCategories) {
    queryParams.ExcludedCategories = params.ExcludedCategories;
  }
  if (typeof params.InvestorTagId !== 'undefined') {
    queryParams.InvestorTagId = params.InvestorTagId;
  }
  if (typeof params.TagId !== 'undefined') {
    queryParams.TagId = params.TagId;
  }
  return {
    url: `${buildRoleAwarePrefix(currentRole, params.InvestorId)}/${endpoint}`,
    queryParams,
  };
}

// Holding-specific unrealized gains query parameters interface
export interface UnrealizedGainsQueryParams {
  holdingId: number;
  limit?: number;
  sort?: Array<{
    property: string;
    direction: 'asc' | 'desc';
    order: number;
  }>;
  investorId?: number;
}

// Holding-specific realized gains query parameters interface
export interface RealizedGainsQueryParams extends UnrealizedGainsQueryParams {
  startDate: string;
  endDate: string;
}

export interface HoldingDetailsQueryParams extends HoldingsQueryParams {
  holdingId: number;
  startDate?: string;
  endDate?: string;
}

export interface HoldingDiversificationQueryParams {
  holdingId: number;
  asOfDate?: string;
  diversificationId?: number;
  categoryId?: number;
  investorId?: number;
}

export interface HoldingDiversification {
  // Define the structure based on the actual API response
  [key: string]: unknown;
}

export interface HoldingDiversificationDetailsQueryParams extends HoldingsQueryParams {
  holdingId: number;
  diversificationId?: number;
  investorId?: number;
}

export interface AlsoHeldInQueryParams extends HoldingsQueryParams {
  alsoHeldHoldingId: number;
}

// Component-specific interfaces for better type safety
export interface HoldingDataField {
  title: string;
  value: ReactNode;
  tooltip?: string;
  isEditable?: boolean;
  onClick?: () => void;
}

export interface HoldingTabConfig {
  id: string;
  title: string;
}

/**
 * Whether a cached holdings entry holds live values rather than a dated snapshot.
 *
 * Cache entries are keyed by `asOfDate` as well as holding id, so a flyout opened on a past
 * date leaves a snapshot sitting beside the live record. A snapshot must never become the body
 * of the live PUT, and must not be patched with a figure that belongs to today. A date of today
 * counts as live, which is how the API reads it too.
 */
const holdsLiveValues = (args: { asOfDate?: string }): boolean =>
  isLiveAsOfDate(args.asOfDate);

export const holdingsApi = createApi({
  reducerPath: 'holdingsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['Holdings'],
  keepUnusedDataFor: 600,
  endpoints: (builder) => ({
    getHoldings: builder.query<
      RecordResponse<HoldingViewModel>,
      HoldingsQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${String(currentRole)}/holdings`,
          params: {
            ...params,
            ...(typeof params.InvestorId !== 'undefined'
              ? { InvestorId: params.InvestorId }
              : {}),
            ...(typeof params.OnlyPrimaryInvestors !== 'undefined'
              ? { OnlyPrimaryInvestors: params.OnlyPrimaryInvestors }
              : {}),
            ...(params.ExcludedAccounts
              ? { ExcludedAccounts: params.ExcludedAccounts }
              : {}),
            ...(params.ExcludedAccountTypes
              ? { ExcludedAccountTypes: params.ExcludedAccountTypes }
              : {}),
            ...(params.ExcludedCategories
              ? { ExcludedCategories: params.ExcludedCategories }
              : {}),
            ...(typeof params.InvestorTagId !== 'undefined'
              ? { InvestorTagId: params.InvestorTagId }
              : {}),
          },
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<HoldingViewModel> };
      },
      providesTags: (result) =>
        result
          ? [
              // Provides 'LIST' tag for the list
              { type: 'Holdings' as const, id: 'LIST' },
              // Provides individual tags for each holding
              ...result.Records.map(({ HoldingId }) => ({
                type: 'Holdings' as const,
                id: HoldingId,
              })),
            ]
          : [{ type: 'Holdings' as const, id: 'LIST' }],
      extraOptions: { maxRetries: 3 },
    }),

    getHoldingsHistoricalTrend: builder.query<
      RecordResponse<HoldingsTrendItem>,
      HoldingsHistoricalTrendQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // Viewing-tag filter is merged by `useGetHoldingsHistoricalTrendQuery`
        // (same as `useGetHoldingsQuery`). Page dates are `MM/DD/YYYY`; this
        // endpoint binds ISO `YYYY-MM-DD` the same way cash-flow / PFS do.
        const result = await baseQuery({
          url: `/api/${String(currentRole)}/HoldingsHistoricalTrend`,
          params: {
            ...params,
            ...(params.startDate
              ? { startDate: toApiDateParam(params.startDate) }
              : {}),
            ...(params.endDate
              ? { endDate: toApiDateParam(params.endDate) }
              : {}),
            ...(params.asOfDate
              ? { asOfDate: toApiDateParam(params.asOfDate) }
              : {}),
            ...(typeof params.InvestorId !== 'undefined'
              ? { InvestorId: params.InvestorId }
              : {}),
            ...(params.ExcludedAccounts
              ? { ExcludedAccounts: params.ExcludedAccounts }
              : {}),
            ...(params.ExcludedAccountTypes
              ? { ExcludedAccountTypes: params.ExcludedAccountTypes }
              : {}),
            ...(params.ExcludedCategories
              ? { ExcludedCategories: params.ExcludedCategories }
              : {}),
            ...(typeof params.InvestorTagId !== 'undefined'
              ? { InvestorTagId: params.InvestorTagId }
              : {}),
          },
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<HoldingsTrendItem> };
      },
      providesTags: [{ type: 'Holdings', id: 'LIST' }],
      extraOptions: { maxRetries: 3 },
    }),

    getHoldingHistoricalTrend: builder.query<
      RecordResponse<HoldingsTrendItem>,
      HoldingHistoricalTrendQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const queryParams: Record<string, unknown> = {
          startDate: params.startDate,
          endDate: params.endDate,
          searchTerm: params.searchTerm,
          page: params.page ?? 1,
          asOfDate: params.asOfDate,
          investorId: params.investorId,
        };

        if (params.sort && params.sort.length > 0) {
          params.sort.forEach((sortItem, index) => {
            queryParams[`sort[${String(index)}].Property`] = sortItem.Property;
            queryParams[`sort[${String(index)}].Direction`] =
              sortItem.Direction;
            queryParams[`sort[${String(index)}].Order`] = sortItem.Order;
          });
        }

        const result = await baseQuery({
          url: `/api/${String(currentRole)}/HoldingsHistoricalTrend/${String(params.holdingId)}`,
          params: queryParams,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<HoldingsTrendItem> };
      },
      extraOptions: { maxRetries: 3 },
    }),

    getUnrealizedGains: builder.query<
      RecordResponse<UnrealizedGainsRecord>,
      PortfolioUnrealizedGainsQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const mergedParams = mergeViewingInvestorTagIntoPortfolioGainsArg(
          params,
          confirmedViewingTagSelection(state),
        );
        const { url, queryParams } = buildPortfolioGainsRequest(
          state.auth.currentRole,
          mergedParams,
          'unrealizedGains',
        );

        const result = await baseQuery({
          url,
          params: queryParams,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<UnrealizedGainsRecord> };
      },
      providesTags: [{ type: 'Holdings', id: 'LIST' }],
      extraOptions: { maxRetries: 3 },
    }),

    getRealizedGainsSummary: builder.query<
      RealizedGainsSummaryResponse,
      PortfolioUnrealizedGainsQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const mergedParams = mergeViewingInvestorTagIntoPortfolioGainsArg(
          params,
          confirmedViewingTagSelection(state),
        );
        const { url, queryParams } = buildPortfolioGainsRequest(
          state.auth.currentRole,
          mergedParams,
          'realizedGainsSummary',
        );

        const result = await baseQuery({
          url,
          params: queryParams,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RealizedGainsSummaryResponse };
      },
      providesTags: [{ type: 'Holdings', id: 'LIST' }],
      extraOptions: { maxRetries: 3 },
    }),

    getRealizedGains: builder.query<
      RecordResponse<RealizedGainsRecord>,
      PortfolioRealizedGainsQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const mergedParams = mergeViewingInvestorTagIntoPortfolioGainsArg(
          params,
          confirmedViewingTagSelection(state),
        );
        const { url, queryParams } = buildPortfolioGainsRequest(
          state.auth.currentRole,
          mergedParams,
          'realizedGains',
        );
        queryParams.startDate = mergedParams.startDate;
        queryParams.endDate = mergedParams.endDate;

        const result = await baseQuery({
          url,
          params: queryParams,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<RealizedGainsRecord> };
      },
      providesTags: [{ type: 'Holdings', id: 'LIST' }],
      extraOptions: { maxRetries: 3 },
    }),

    // Holdings specific unrealized gains
    getHoldingUnrealizedGains: builder.query<
      RecordResponse<UnrealizedGainsRecord>,
      UnrealizedGainsQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const queryParams: Record<string, unknown> = {
          ...(params.investorId !== undefined
            ? { investorId: params.investorId }
            : {}),
        };

        if (params.sort && params.sort.length > 0) {
          params.sort.forEach((sortItem, index) => {
            queryParams[`sort[${String(index)}].Property`] = sortItem.property;
            queryParams[`sort[${String(index)}].Direction`] =
              sortItem.direction;
            queryParams[`sort[${String(index)}].Order`] = sortItem.order;
          });
        }

        const url = `/api/${String(currentRole)}/Holdings/${String(params.holdingId)}/unrealizedGains`;

        const result = await baseQuery({
          url,
          params: queryParams,
          method: 'GET',
        });

        if (result.error) return { error: result.error };
        return { data: result.data as RecordResponse<UnrealizedGainsRecord> };
      },
      extraOptions: { maxRetries: 3 },
    }),

    // Holdings specific realized gains summary
    getHoldingRealizedGainsSummary: builder.query<
      RealizedGainsSummaryResponse,
      RealizedGainsQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const queryParams: Record<string, unknown> = {
          startDate: params.startDate,
          endDate: params.endDate,
          ...(params.investorId !== undefined
            ? { investorId: params.investorId }
            : {}),
        };

        if (params.sort && params.sort.length > 0) {
          params.sort.forEach((sortItem, index) => {
            queryParams[`sort[${String(index)}].Property`] = sortItem.property;
            queryParams[`sort[${String(index)}].Direction`] =
              sortItem.direction;
            queryParams[`sort[${String(index)}].Order`] = sortItem.order;
          });
        }

        const url = `/api/${String(currentRole)}/Holdings/${String(params.holdingId)}/realizedGainsSummary`;

        const result = await baseQuery({
          url,
          params: queryParams,
          method: 'GET',
        });

        if (result.error) return { error: result.error };
        return { data: result.data as RealizedGainsSummaryResponse };
      },
      extraOptions: { maxRetries: 3 },
    }),

    // Holdings specific realized gains
    getHoldingRealizedGains: builder.query<
      RecordResponse<RealizedGainsRecord>,
      RealizedGainsQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const queryParams: Record<string, unknown> = {
          startDate: params.startDate,
          endDate: params.endDate,
          ...(params.investorId !== undefined
            ? { investorId: params.investorId }
            : {}),
        };

        if (params.sort && params.sort.length > 0) {
          params.sort.forEach((sortItem, index) => {
            queryParams[`sort[${String(index)}].Property`] = sortItem.property;
            queryParams[`sort[${String(index)}].Direction`] =
              sortItem.direction;
            queryParams[`sort[${String(index)}].Order`] = sortItem.order;
          });
        }

        const url = `/api/${String(currentRole)}/Holdings/${String(params.holdingId)}/realizedGains`;

        const result = await baseQuery({
          url,
          params: queryParams,
          method: 'GET',
        });

        if (result.error) return { error: result.error };
        return { data: result.data as RecordResponse<RealizedGainsRecord> };
      },
      extraOptions: { maxRetries: 3 },
    }),

    /**
     * A single holding, valued as of `asOfDate` when one is given.
     *
     * The holding detail flyout inherits the as-of date from whatever opened it, and the
     * fields it shows — balance, quantity, price, cost basis, last updated — are read from
     * history rather than from the live holding when that date is in the past. The parameter
     * is omitted rather than sent empty, because the API treats its absence as "today".
     */
    getHoldingDetails: builder.query<
      HoldingViewModel,
      HoldingDetailsQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // Build query parameters to match legacy UI
        const queryParams: Record<string, unknown> = {
          startDate: params.startDate ?? '',
          endDate: params.endDate ?? '',
          searchTerm: params.searchTerm ?? '',
          page: params.page ?? 1,
          _: Date.now(), // Cache buster timestamp like legacy UI
        };

        if (params.asOfDate) {
          queryParams.asOfDate = params.asOfDate;
        }

        // Handle sort parameters (use legacy format with brackets)
        if (params.sort && params.sort.length > 0) {
          params.sort.forEach((sortItem, index) => {
            queryParams[`sort[${String(index)}][Property]`] = sortItem.Property;
            queryParams[`sort[${String(index)}][Direction]`] =
              sortItem.Direction;
            queryParams[`sort[${String(index)}][Order]`] = sortItem.Order;
          });
        } else {
          // Default sort like legacy UI
          queryParams[`sort[0][Property]`] = 'Balance';
          queryParams[`sort[0][Direction]`] = 'desc';
          queryParams[`sort[0][Order]`] = 0;
        }

        const result = await baseQuery({
          url: `/api/${String(currentRole)}/holdings/${String(params.holdingId)}`,
          params: queryParams,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as HoldingViewModel };
      },
      providesTags: (_, __, { holdingId }) => [
        { type: 'Holdings' as const, id: holdingId },
      ],
      extraOptions: { maxRetries: 3 },
    }),

    getHoldingDiversification: builder.query<
      HoldingDiversification,
      HoldingDiversificationQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${String(currentRole)}/HoldingDiversification/${String(params.holdingId)}`,
          params: {
            asOfDate: params.asOfDate,
            diversificationId: params.diversificationId,
            categoryId: params.categoryId,
            ...(params.investorId !== undefined
              ? { investorId: params.investorId }
              : {}),
          },
          method: 'GET',
        });

        if (result.error) return { error: result.error };
        return { data: result.data as HoldingDiversification };
      },
    }),

    getHoldingDiversificationDetails: builder.query<
      RecordResponse<HoldingViewModel>,
      HoldingDiversificationDetailsQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${String(currentRole)}/HoldingDiversification/${String(params.holdingId)}`,
          params: (() => {
            const queryParams: Record<string, unknown> = {
              asOfDate: params.asOfDate,
              diversificationId: params.diversificationId,
              holdingId: params.holdingId,
              searchTerm: params.searchTerm,
              page: params.page ?? 1,
              investorId: params.investorId,
            };

            if (params.sort && params.sort.length > 0) {
              params.sort.forEach((sortItem, index) => {
                queryParams[`sort[${String(index)}].Property`] =
                  sortItem.Property;
                queryParams[`sort[${String(index)}].Direction`] =
                  sortItem.Direction;
                queryParams[`sort[${String(index)}].Order`] = sortItem.Order;
              });
            }

            return queryParams;
          })(),
          method: 'GET',
        });

        if (result.error) return { error: result.error };
        return { data: result.data as RecordResponse<HoldingViewModel> };
      },
    }),

    // Fetch holdings that are also held in other accounts/investments based on a specific holdingId
    getAlsoHeldHoldings: builder.query<
      RecordResponse<HoldingViewModel>,
      AlsoHeldInQueryParams
    >({
      queryFn: async (params, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // Build query parameters ensuring the sort array is serialized the way the legacy API expects
        const queryParams: Record<string, unknown> = {
          alsoHeldHoldingId: params.alsoHeldHoldingId,
          searchTerm: params.searchTerm ?? '',
          page: params.page ?? 1,
          asOfDate: params.asOfDate,
        };

        // Handle sort parameters (e.g. sort[0].Property, sort[0].Direction, sort[0].Order)
        if (params.sort && params.sort.length > 0) {
          params.sort.forEach((sortItem, index) => {
            queryParams[`sort[${String(index)}].Property`] = sortItem.Property;
            queryParams[`sort[${String(index)}].Direction`] =
              sortItem.Direction;
            queryParams[`sort[${String(index)}].Order`] = sortItem.Order;
          });
        }

        const result = await baseQuery({
          url: `/api/${String(currentRole)}/holdings`,
          params: queryParams,
          method: 'GET',
        });

        if (result.error) return { error: result.error };
        return { data: result.data as RecordResponse<HoldingViewModel> };
      },
      extraOptions: { maxRetries: 3 },
    }),

    /**
     * Updates a holding with optimistic cache updates for immediate UI feedback.
     *
     * OPTIMISTIC UPDATE STRATEGY:
     * This mutation implements a sophisticated optimistic update pattern that provides
     * instant UI feedback while maintaining data consistency and handling errors gracefully.
     *
     * WHY OPTIMISTIC UPDATES?
     * - Immediate user feedback (no waiting for server response)
     * - Better UX for interactive editing (cost basis changes)
     * - Eliminates jarring loading states and page re-renders
     * - Maintains data consistency across multiple cache entries
     *
     * HOW IT WORKS:
     * 1. User triggers update → UI updates instantly via cache manipulation
     * 2. Server request happens in background
     * 3. Success → Cache stays updated
     * 4. Failure → Automatic rollback to original values
     *
     * CACHE STRATEGY:
     * - Uses cache-first approach for PUT request data (avoids race conditions)
     * - Falls back to server fetch if cache miss
     * - Updates ALL relevant cache entries (getHoldingDetails + getHoldings)
     * - Handles complex query parameter combinations automatically
     *
     * SAFETY MEASURES:
     * - Automatic rollback on server failures
     * - Cache expires after 60 seconds of non-use
     * - No persistence across browser sessions
     * - Server fallback ensures data integrity
     */
    updateHolding: builder.mutation<
      boolean,
      Partial<HoldingViewModel> & { HoldingId: number }
    >({
      queryFn: async (updatedFields, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const { HoldingId } = updatedFields;

        // CACHE-FIRST APPROACH: Try to get current holding data from cache first
        // This prevents race conditions when users make rapid successive updates
        // and ensures PUT requests use the most current cached data
        let currentHolding: HoldingViewModel | null = null;

        // Search through all cached getHoldingDetails queries to find our holding
        const cachedDetails = getCachedEntriesForEndpoint(
          state,
          holdingsApi,
          'getHoldingDetails',
        );
        const matchingDetails = cachedDetails.find(
          (entry) =>
            entry.originalArgs.holdingId === HoldingId &&
            holdsLiveValues(entry.originalArgs),
        );
        if (matchingDetails) {
          currentHolding = matchingDetails.data;
        }

        // FALLBACK: If not found in cache, fetch fresh data from server
        // This ensures we always have current data even if cache is empty
        if (!currentHolding) {
          const holdingResult = await baseQuery({
            url: `/api/${String(currentRole)}/holdings/${String(HoldingId)}`,
            method: 'GET',
          });

          if (holdingResult.error) {
            return { error: holdingResult.error };
          }

          currentHolding = holdingResult.data as HoldingViewModel;
        }

        // Merge updated fields with current data (legacy API expects full object)
        const mergedHolding = { ...currentHolding, ...updatedFields };

        // Send PUT request to server with merged data
        const result = await baseQuery({
          url: `/api/${String(currentRole)}/holdings/${String(HoldingId)}`,
          method: 'PUT',
          body: mergedHolding,
        });

        if (result.error) return { error: result.error };
        return { data: result.data as boolean };
      },
      /**
       * OPTIMISTIC UPDATE IMPLEMENTATION
       *
       * This function runs immediately when the mutation is triggered, before waiting
       * for the server response. It updates all relevant cache entries instantly to
       * provide immediate UI feedback.
       *
       * CACHE DISCOVERY STRATEGY:
       * RTK Query can have multiple cache entries for the same data with different
       * query parameters (e.g., different asOfDate, page, sort, etc.). We need to
       * find and update ALL cache entries that contain our holding.
       *
       * ROLLBACK MECHANISM:
       * All cache updates return "patches" that can be undone if the server request
       * fails, ensuring automatic rollback to original values.
       */
      async onQueryStarted(updatedFields, api) {
        const { dispatch, queryFulfilled } = api;
        const { HoldingId } = updatedFields;

        // Store all cache update patches for potential rollback on server failure
        const patches: Array<{ undo: () => void }> = [];

        try {
          const state = api.getState();

          // UPDATE HOLDING DETAILS CACHE:
          // Find getHoldingDetails queries that match our holding ID
          for (const entry of getCachedEntriesForEndpoint(
            state,
            holdingsApi,
            'getHoldingDetails',
          )) {
            if (
              entry.originalArgs.holdingId !== HoldingId ||
              !holdsLiveValues(entry.originalArgs)
            ) {
              continue;
            }
            // Update the cached holding details immediately
            const patch = dispatch(
              holdingsApi.util.updateQueryData(
                'getHoldingDetails',
                entry.originalArgs,
                (draft) => {
                  Object.assign(draft, updatedFields);
                },
              ),
            );
            patches.push(patch);
          }

          // UPDATE HOLDINGS LIST CACHE:
          // Find getHoldings queries that contain our holding in their Records array
          for (const entry of getCachedEntriesForEndpoint(
            state,
            holdingsApi,
            'getHoldings',
          )) {
            // Check if this holdings list contains our holding
            const holdingIndex = entry.data.Records.findIndex(
              (h) => h.HoldingId === HoldingId,
            );
            if (holdingIndex === -1 || !holdsLiveValues(entry.originalArgs)) {
              continue;
            }
            // Update the holding within the holdings list immediately
            const patch = dispatch(
              holdingsApi.util.updateQueryData(
                'getHoldings',
                entry.originalArgs,
                (draft) => {
                  const targetIndex = draft.Records.findIndex(
                    (holding) => holding.HoldingId === HoldingId,
                  );
                  if (targetIndex !== -1) {
                    Object.assign(draft.Records[targetIndex], updatedFields);
                  }
                },
              ),
            );
            patches.push(patch);
          }

          // WAIT FOR SERVER RESPONSE:
          // All optimistic updates are now applied. Wait for server confirmation.
          await queryFulfilled;

          // SUCCESS: Server update completed, optimistic updates remain in place
        } catch (error) {
          // ROLLBACK ON FAILURE:
          // If server request fails, automatically undo all optimistic updates
          // This restores the original values in all affected cache entries
          patches.forEach((patch) => {
            patch.undo();
          });

          console.error(
            'Failed to update holding, rolling back optimistic updates:',
            error,
          );

          // The error will bubble up to the component for user feedback
        }
      },
      // NOTE: invalidatesTags is intentionally removed to prevent full page re-renders.
      // The optimistic updates above handle all cache synchronization automatically.
    }),
  }),
});

export const {
  useGetHoldingsQuery: useGetHoldingsQueryBase,
  useGetHoldingsHistoricalTrendQuery: useGetHoldingsHistoricalTrendQueryBase,
  useGetHoldingHistoricalTrendQuery,
  useGetUnrealizedGainsQuery: useGetUnrealizedGainsQueryBase,
  useGetRealizedGainsSummaryQuery: useGetRealizedGainsSummaryQueryBase,
  useGetRealizedGainsQuery: useGetRealizedGainsQueryBase,
  useGetHoldingUnrealizedGainsQuery,
  useGetHoldingRealizedGainsSummaryQuery,
  useGetHoldingRealizedGainsQuery,
  useLazyGetHoldingRealizedGainsQuery,
  useLazyGetHoldingUnrealizedGainsQuery,
  useGetHoldingDetailsQuery,
  useGetHoldingDiversificationQuery,
  useGetHoldingDiversificationDetailsQuery,
  useGetAlsoHeldHoldingsQuery,
  useUpdateHoldingMutation,
} = holdingsApi;

type GetHoldingsUseQueryOptions = Parameters<
  typeof holdingsApi.endpoints.getHoldings.useQuery
>[1];

type GetPortfolioUnrealizedGainsUseQueryOptions = Parameters<
  typeof holdingsApi.endpoints.getUnrealizedGains.useQuery
>[1];

// Forwarding the full `Parameters<...>[1]` options bag through the wrapper
// widens `selectFromResult` and collapses the `useQuery` return type to `any`.
// Accept only subscription options and pin the result type explicitly.
type GetPortfolioRealizedGainsSummaryUseQueryOptions =
  TypedUseQuerySubscriptionOptions;

type GetPortfolioRealizedGainsSummaryQueryResult = TypedUseQueryHookResult<
  RealizedGainsSummaryResponse,
  PortfolioUnrealizedGainsQueryParams,
  typeof waServicesQuery
>;

type GetPortfolioRealizedGainsUseQueryOptions = Parameters<
  typeof holdingsApi.endpoints.getRealizedGains.useQuery
>[1];

export function useGetHoldingsQuery(
  arg: HoldingsQueryParams | typeof skipToken,
  options?: GetHoldingsUseQueryOptions,
) {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : mergeViewingInvestorTagIntoHoldingsArg(arg, confirmed.selection);

  return holdingsApi.endpoints.getHoldings.useQuery(queryArg, options);
}

// Forwarding the full `Parameters<...>[1]` options bag through the wrapper
// widens `selectFromResult` and collapses the `useQuery` return type to `any`.
// Accept only subscription options and pin the result type explicitly.
type GetHoldingsHistoricalTrendUseQueryOptions =
  TypedUseQuerySubscriptionOptions;

type GetHoldingsHistoricalTrendQueryResult = TypedUseQueryHookResult<
  RecordResponse<HoldingsTrendItem>,
  HoldingsHistoricalTrendQueryParams,
  typeof waServicesQuery
>;

/**
 * Same as the RTK `getHoldingsHistoricalTrend` subscription, but
 * automatically merges the source-aware `viewingTag.selectedViewingTag`
 * selection as `InvestorTagId` (System A). Explicit tag filters and
 * advisor-scoped `InvestorId` requests remain untouched.
 */
export function useGetHoldingsHistoricalTrendQuery(
  arg: HoldingsHistoricalTrendQueryParams | typeof skipToken,
  options?: GetHoldingsHistoricalTrendUseQueryOptions,
): GetHoldingsHistoricalTrendQueryResult {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : mergeViewingInvestorTagIntoHoldingsHistoricalTrendArg(
          arg,
          confirmed.selection,
        );

  return holdingsApi.endpoints.getHoldingsHistoricalTrend.useQuery(
    queryArg,
    options,
  );
}

export function useGetUnrealizedGainsQuery(
  arg: PortfolioUnrealizedGainsQueryParams | typeof skipToken,
  options?: GetPortfolioUnrealizedGainsUseQueryOptions,
) {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildPortfolioGainsSubscriptionArg(arg, confirmed.selection);

  return holdingsApi.endpoints.getUnrealizedGains.useQuery(queryArg, options);
}

export function useGetRealizedGainsSummaryQuery(
  arg: PortfolioUnrealizedGainsQueryParams | typeof skipToken,
  options?: GetPortfolioRealizedGainsSummaryUseQueryOptions,
): GetPortfolioRealizedGainsSummaryQueryResult {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildPortfolioGainsSubscriptionArg(arg, confirmed.selection);

  return holdingsApi.endpoints.getRealizedGainsSummary.useQuery(
    queryArg,
    options,
  );
}

export function useGetRealizedGainsQuery(
  arg: PortfolioRealizedGainsQueryParams | typeof skipToken,
  options?: GetPortfolioRealizedGainsUseQueryOptions,
) {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildPortfolioGainsSubscriptionArg(arg, confirmed.selection);

  return holdingsApi.endpoints.getRealizedGains.useQuery(queryArg, options);
}
